import * as XLSX from 'xlsx';
import { newHeatingSystem } from './defaults';

const FIRST_MONTH_COL_INDEX_0 = 3; // Spalte D
const MONTH_HEADER_ROW_1_BASED = 10;

const SECTIONS = {
  elektrizitaet: {
    anchorRow1Based: 10,
    gesamtRowOffsets: [13, 23, 32, 41, 50],
    keysInOrder: [
      'strom_kwh_total',
      'solar_sell_kwh_total',
      'solar_self_kwh_total',
      'bhkw_sell_kwh_total',
      'bhkw_self_kwh_total',
    ],
  },
  heizung: {
    anchorRow1Based: 70,
    gesamtRowOffsets: [13, 22],
    keysInOrder: ['heizung_gas_kwh_total', 'heizung_oel_kwh_total'],
  },
  produktion: {
    anchorRow1Based: 93,
    gesamtRowOffsets: [9, 19, 29, 39],
    keysInOrder: [
      'produktion_stunden_kombi_total',
      'produktion_stunden_lack_total',
      'produktion_stunden_karos_total',
      'anzahl_reparaturen_total',
    ],
  },
  rohstoffe: {
    anchorRow1Based: 133,
    gesamtRowOffsets: [8],
    keysInOrder: ['lack_liter_total'],
  },
};

const GEBAEUDE_M2_ROW1_BASED = 163; // "Gesamt"-Zeile unter "GEBÄUDE, KABINE(N)"
const GEBAEUDE_M2_COL_INDEX_0 = 1; // Spalte B ("m2 Gebäude, total")

function parseLocalizedNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;

  let str = String(value).trim().replace(/[^0-9,.\-]/g, '');

  if (str.includes('.') && str.includes(',')) {
    str = str.replace(/\./g, '').replace(',', '.');
  } else if (/,(\d{3})$/.test(str)) {
    str = str.replace(/,/g, '');
  } else if (str.includes(',')) {
    str = str.replace(',', '.');
  }

  const n = Number(str);
  return Number.isFinite(n) ? n : null;
}

function normalize(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();
}

function buildColYearMap(headerRow, startCol0) {
  const colYear = {};

  for (let c = startCol0; c < headerRow.length; c++) {
    const cell = headerRow[c];

    if (typeof cell === 'string') {
      const match = cell.match(/(20\d{2}|\d{2})\s*$/);

      if (match) {
        let year = parseInt(match[1], 10);
        if (year < 100) year += 2000;
        colYear[c] = year;
        continue;
      }

      const parsedDate = new Date(cell);
      if (!isNaN(parsedDate.getTime())) {
        colYear[c] = parsedDate.getFullYear();
        continue;
      }
    }

    if (typeof cell === 'number') {
      const dateCode = XLSX.SSF.parse_date_code(cell);
      if (dateCode?.y) {
        colYear[c] = dateCode.y;
        continue;
      }
    }

    if (cell instanceof Date && !isNaN(cell.getTime())) {
      colYear[c] = cell.getFullYear();
    }
  }

  return colYear;
}

function sumRowByYear(row, startCol0, colYearMap) {
  const out = {};

  for (let c = startCol0; c < row.length; c++) {
    const year = colYearMap[c];
    if (!year) continue;

    const value = parseLocalizedNumber(row[c]);
    if (value == null) continue;

    out[year] = (out[year] || 0) + value;
  }

  return out;
}

function mergeYearSums(target, add) {
  for (const [yearStr, value] of Object.entries(add)) {
    const year = Number(yearStr);
    target[year] = (target[year] || 0) + value;
  }
}

function detectCarbonPulseSheet(workbook, file) {
  const name = normalize(file?.name || '');
  const looksLikeCarbonPulse =
    name.includes('carbon pulse') || name.includes('carbonpulse');

  const checkSheet = (workbook.SheetNames || []).find(sheet =>
    normalize(sheet).includes('check')
  );

  if (!looksLikeCarbonPulse && !checkSheet) return null;

  return (
    workbook.Sheets['Check-Liste'] ? 'Check-Liste' : checkSheet
  ) || workbook.SheetNames[0];
}

export async function parseCarbonPulseExcelFile(file) {
  if (!file) {
    throw new Error('Bitte wählen Sie eine Excel-Datei aus.');
  }

  const extension = file.name.split('.').pop()?.toLowerCase();

  if (!['xlsx', 'xls'].includes(extension)) {
    throw new Error(
      'Ungültiges Dateiformat. Erlaubt sind nur XLSX- und XLS-Dateien.'
    );
  }

  let workbook;

  try {
    const arrayBuffer = await file.arrayBuffer();
    workbook = XLSX.read(arrayBuffer, { type: 'array' });
  } catch {
    throw new Error('Die Excel-Datei konnte nicht gelesen werden.');
  }

  const sheetName = detectCarbonPulseSheet(workbook, file);

  if (!sheetName) {
    throw new Error(
      'Diese Datei sieht nicht nach einer Carbonpulse Vorab-Checkliste aus. Erwartet wird ein Tabellenblatt „Check-Liste“.'
    );
  }

  const worksheet = workbook.Sheets[sheetName];

  const rows = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    blankrows: true,
    raw: false,
  });

  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error('Das Tabellenblatt „' + sheetName + '“ ist leer.');
  }

  const headerRowIndex = MONTH_HEADER_ROW_1_BASED - 1;
  const headerRow = rows[headerRowIndex] || [];
  const colYear = buildColYearMap(headerRow, FIRST_MONTH_COL_INDEX_0);

  const jahreswerte = {};
  const allYears = new Set();

  for (const section of Object.values(SECTIONS)) {
    const anchorRow0 = section.anchorRow1Based - 1;

    // Jede Sektion hat ihre eigene Kopfzeile (z. B. monatlich Jan-22..Dec-25
    // oder, wie bei Rohstoffe, nur jährlich 2020..2025) – daher pro Sektion
    // eine eigene Spalten-Jahr-Zuordnung statt der globalen Monats-Kopfzeile
    // verwenden.
    const sectionHeaderRow = rows[anchorRow0] || [];
    const sectionColYear = buildColYearMap(
      sectionHeaderRow,
      FIRST_MONTH_COL_INDEX_0
    );

    Object.values(sectionColYear).forEach(year => allYears.add(year));

    section.gesamtRowOffsets.forEach((offset, index) => {
      const key = section.keysInOrder[index];
      const row = rows[anchorRow0 + offset] || [];
      const perYear = sumRowByYear(row, FIRST_MONTH_COL_INDEX_0, sectionColYear);

      if (!jahreswerte[key]) jahreswerte[key] = {};
      mergeYearSums(jahreswerte[key], perYear);
    });
  }

  Object.values(colYear).forEach(year => allYears.add(year));

  const availableYears = [...allYears].filter(Boolean).sort((a, b) => a - b);

  if (availableYears.length === 0) {
    throw new Error(
      'In der Datei konnten keine Jahresspalten erkannt werden.'
    );
  }

  const gebaeudeM2Row = rows[GEBAEUDE_M2_ROW1_BASED - 1] || [];
  const gebaeudeM2 = parseLocalizedNumber(
    gebaeudeM2Row[GEBAEUDE_M2_COL_INDEX_0]
  );

  return {
    availableYears,
    referenzjahr: availableYears.at(-1),
    jahreswerte,
    gebaeudeM2: gebaeudeM2 != null && gebaeudeM2 > 0 ? gebaeudeM2 : null,
  };
}

function valueForYear(jahreswerte, key, year) {
  const value = jahreswerte?.[key]?.[year];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function buildCarbonPulseFormPatch(jahreswerte, year, gebaeudeM2) {
  const patch = {};
  const importedFields = [];

  if (gebaeudeM2 != null) {
    patch.totalAreaM2 = gebaeudeM2;
    importedFields.push('totalAreaM2');
  }

  const strom = valueForYear(jahreswerte, 'strom_kwh_total', year);
  if (strom != null) {
    patch.electricityValue = strom;
    patch.electricityUnit = 'kwh';
    importedFields.push('electricityValue', 'electricityUnit');
  }

  const solarSelf = valueForYear(jahreswerte, 'solar_self_kwh_total', year) || 0;
  const solarSell = valueForYear(jahreswerte, 'solar_sell_kwh_total', year) || 0;

  if (solarSelf > 0 || solarSell > 0) {
    patch.pvStatus = 'vorhanden';
    patch.pvProductionKwh = solarSelf + solarSell;
    patch.pvFeedInKwh = solarSell;
    importedFields.push('pvStatus', 'pvProductionKwh', 'pvFeedInKwh');
  }

  const gas = valueForYear(jahreswerte, 'heizung_gas_kwh_total', year);
  const oel = valueForYear(jahreswerte, 'heizung_oel_kwh_total', year);

  const heatingSystems = [];
  if (gas != null && gas > 0) {
    heatingSystems.push({
      ...newHeatingSystem(),
      type: 'Erdgas',
      unit: 'kwh',
      value: gas,
      fromImport: true,
    });
  }
  if (oel != null && oel > 0) {
    heatingSystems.push({
      ...newHeatingSystem(),
      type: 'Heizöl',
      unit: 'kwh',
      value: oel,
      fromImport: true,
    });
  }
  if (heatingSystems.length > 0) {
    patch.heatingSystems = heatingSystems;
    importedFields.push('heatingSystems');
  }

  const soldHoursKombi = valueForYear(
    jahreswerte,
    'produktion_stunden_kombi_total',
    year
  );
  const soldHoursLack = valueForYear(
    jahreswerte,
    'produktion_stunden_lack_total',
    year
  );
  const soldHoursKaros = valueForYear(
    jahreswerte,
    'produktion_stunden_karos_total',
    year
  );

  // Lackiererei- und Karosserie-Stunden werden im Excel unabhängig von der
  // "kombiniert"-Zeile erfasst und stimmen mit deren Summe oft nicht exakt
  // überein. Die Eingabemaske verlangt aber Paint + Body === Total, daher
  // wird der Total-Wert aus der Aufteilung abgeleitet statt die eigene
  // "kombiniert"-Zeile zu übernehmen.
  if (soldHoursLack != null && soldHoursKaros != null) {
    const paintHours = Math.round(soldHoursLack);
    const bodyHours = Math.round(soldHoursKaros);
    patch.splitSoldHours = true;
    patch.soldHoursPaint = paintHours;
    patch.soldHoursBody = bodyHours;
    patch.soldHoursTotal = paintHours + bodyHours;
    importedFields.push(
      'splitSoldHours',
      'soldHoursPaint',
      'soldHoursBody',
      'soldHoursTotal'
    );
  } else if (soldHoursKombi != null) {
    patch.soldHoursTotal = Math.round(soldHoursKombi);
    importedFields.push('soldHoursTotal');
  }

  const paintLiters = valueForYear(jahreswerte, 'lack_liter_total', year);
  if (paintLiters != null) {
    patch.paintLiters = paintLiters;
    importedFields.push('paintLiters');
  }

  const repairsPerYear = valueForYear(
    jahreswerte,
    'anzahl_reparaturen_total',
    year
  );
  if (repairsPerYear != null) {
    patch.repairsPerYear = Math.round(repairsPerYear);
    importedFields.push('repairsPerYear');
  }

  return { patch, importedFields };
}
